import { BasicObservableInterop } from "./BasicObservableInterop.js";
import { normalizeSubscriber } from "./normalizeSubscriber.js";
import { OBSERVABLE_PROPERTY_NAME } from "./ObservableProtocol.js";
/**
 * An `Observable` implementation that can be used to emit values
 * and notify subscribers.
 *
 * @internal scope: workspace
 */
export class Beacon {
    static withEmitter() {
        let emitter;
        const beacon = new Beacon((observer) => {
            emitter = observer;
        });
        return {
            next: (value) => {
                emitter.next(value);
            },
            complete: () => {
                emitter.complete();
            },
            error: (error) => {
                emitter.error(error);
            },
            get hasTerminated() {
                return emitter.hasTerminated;
            },
            beacon,
        };
    }
    #isTerminated = false;
    /**
     * A set of weak references to the subscribers.
     *
     * @remarks
     *
     * Subscriptions are weakly referenced to prevent memory leaks,
     * as a result, we can't normalize the subscribe input to an observer,
     * because the observer would be garbage collected.
     * We have to normalize the subscribe input when the weak reference
     * is dereferenced.
     */
    #subscribers = new Set();
    constructor(executor) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const beacon = this;
        executor({
            next: (value) => {
                this.#emitNextSignal(value);
            },
            complete: () => {
                this.#emitCompleteSignal();
            },
            error: (error) => {
                this.#emitErrorSignal(error);
            },
            get hasTerminated() {
                return beacon.#isTerminated;
            },
        });
    }
    /**
     * Subscribes to updates from the observable.
     */
    subscribe(subscriber) {
        const weakSubscriber = new WeakRef(subscriber);
        this.#subscribers.add(weakSubscriber);
        return {
            unsubscribe: () => {
                this.#subscribers.delete(weakSubscriber);
            },
        };
    }
    /**
     * Synchronously notify all subscribers of a new value.
     */
    #emitNextSignal(value) {
        this.#forEachSubscriber((observer) => observer.next(value));
    }
    /**
     * Synchronously notify all subscribers of completion.
     *
     * @remarks
     *
     * Terminates the observable.
     */
    #emitCompleteSignal() {
        this.#forEachSubscriber((observer) => observer.complete?.());
        this.#isTerminated = true;
    }
    /**
     * Synchronously notify all subscribers of an error.
     *
     * @remarks
     *
     * Terminates the observable.
     */
    #emitErrorSignal(error) {
        this.#forEachSubscriber((observer) => observer.error?.(error));
        this.#isTerminated = true;
    }
    #forEachSubscriber(handler) {
        if (this.#isTerminated) {
            // The observable is already terminated. Ignoring notification.
            //
            // NOTE: This intentionally does not throw an error, or log a warning.
            // While all procedures _should_ clean up after a termination, we can avoid
            // writing a lot of `!hasTerminated` checks by making the method a no-op
            // when the observable is already terminated.
            return;
        }
        // Copy the current set of subscribers to prevent issues
        // if the set is modified (e.g., subscribers unsubscribe)
        // during iteration.
        const subscribers = Array.from(this.#subscribers);
        for (const weakSubscriber of subscribers) {
            const subscriber = weakSubscriber.deref();
            if (!subscriber) {
                // The subscriber has been garbage collected, remove it from the set.
                this.#subscribers.delete(weakSubscriber);
                continue;
            }
            handler(normalizeSubscriber(subscriber));
        }
    }
    asInterop() {
        return new BasicObservableInterop(this);
    }
    [Symbol.observable]() {
        return this.asInterop();
    }
    [OBSERVABLE_PROPERTY_NAME]() {
        return this.asInterop();
    }
}
